/**
 * Placement et geometrie des constructions.
 *
 * Le terrain n'est pas modifiable en phase 1: on ne nivelle rien, on refuse
 * simplement les emplacements trop en pente. Les rotations sont des quarts de
 * tour (0..3), ce qui suffit pour un jeu vu de 45 degres et rend les tests
 * d'emprise exacts.
 *
 * Le terrain est recu comme ITerrain: ce module ne depend pas du generateur.
 */
import { CARTE_X, CARTE_Z, PLAYER_RADIUS } from '../constants.ts'
import type { Cout, Inventaire, ItemId } from '../types/items.ts'
import type {
  Batiment,
  BatimentKind,
  BoiteFraction,
  Glissade,
  PieceEtage,
  PlanModeleEtage,
  Ressource,
  RessourceKind,
} from '../types/monde.ts'
import type { ITerrain, Vec2 } from '../types/world.ts'
import { BATIMENTS } from '../data/batiments.ts'
import { LIT_HOTEL, PLAN_HOTEL, TABLE_HOTEL } from '../data/hotel.ts'
import { PLAN_AEROPORT } from '../data/aeroports.ts'
import { PLAN_PARC_AQUATIQUE } from '../data/parc-aquatique.ts'
import { RESSOURCES } from '../data/ressources.ts'
import { PLAN_INTERIEUR, type PieceInterieure } from '../data/zoo.ts'
import { OBJETS } from '../data/objets.ts'
import { clamp, hache2 } from './alea.ts'
import { manquant, peutPayer } from './inventaire.ts'

export interface ValidationPlacement {
  ok: boolean
  /** Message francais affichable tel quel. */
  raison?: string
}

/** Marge libre exigee entre deux emprises, en metres. */
export const MARGE_BATIMENT = 0.5

/** Pente moyenne maximale sous l'emprise. */
const PENTE_MOYENNE_MAX = 0.32

/** Pente ponctuelle maximale sous l'emprise. */
const PENTE_PONCTUELLE_MAX = 0.62

/** Denivele maximal sous l'emprise, en metres. */
const DENIVELE_MAX = 1.6

/** Sous cette surface, on est tolerant (poteau, clôture). */
const SURFACE_PETITE = 2.5

/** Couleurs par defaut de chaque modele. */
/** Couleur neutre pour un modele qui n'a pas de defaut declare. */
export const COULEURS_NEUTRES = { murs: 0xc4b79a, toit: 0x8a6f52, porte: 0x6b5136 }

/**
 * Couleurs par defaut, par modele. PARTIEL a dessein: le catalogue compte une
 * cinquantaine de modeles et c'est le rendu qui tient leur palette; ici on ne
 * garde que ce dont la simulation a besoin, avec un repli neutre.
 */
export const COULEURS_DEFAUT: Partial<Record<BatimentKind, { murs: number; toit: number; porte: number }>> = {
  cabane: { murs: 0xc4a06a, toit: 0x8b5a2b, porte: 0x6b4423 },
  chalet: { murs: 0xd9b183, toit: 0xa33c2f, porte: 0x5e3a22 },
  maisonnette: { murs: 0xf2e2c4, toit: 0x3f6fa5, porte: 0x7a4a2a },
  villa: { murs: 0xfdf6e8, toit: 0xc9553f, porte: 0x4a3320 },
  villa_etage: { murs: 0xf4efe2, toit: 0xb8c4c9, porte: 0x2f5f6b },
  hotel: { murs: 0xf3ead8, toit: 0xc8b89a, porte: 0x8a4a3a },
  igloo: { murs: 0xe8f6ff, toit: 0xcfe8f7, porte: 0x9ec9dd },
  cabane_plage: { murs: 0xffe6a8, toit: 0xff8a5c, porte: 0x9a6136 },
  piscine: { murs: 0x4fc3e8, toit: 0xe8f4f8, porte: 0xe8f4f8 },
  jardin: { murs: 0x6a4a2a, toit: 0x62b04a, porte: 0x62b04a },
  cloture: { murs: 0xb98a55, toit: 0xb98a55, porte: 0xb98a55 },
  lampadaire: { murs: 0x4a4a52, toit: 0xffe9a8, porte: 0x4a4a52 },
  rampe_feux: { murs: 0x6b4a3a, toit: 0xd9534f, porte: 0x6b4a3a },
}

// ------------------------------------------------------------------ geometrie

/** Demi-dimensions de l'emprise apres rotation (quarts de tour). */
export function demiEmprise(kind: BatimentKind, rot: number): { hx: number; hz: number } {
  const t = BATIMENTS[kind].taille
  const quart = ((rot % 4) + 4) % 4
  return quart % 2 === 1 ? { hx: t.p / 2, hz: t.l / 2 } : { hx: t.l / 2, hz: t.p / 2 }
}

/**
 * Tourne un point local (dx, dz) de `rot` quarts de tour autour de l'origine.
 * Convention: rot=1 envoie (1, 0) sur (0, -1).
 */
export function tourner(dx: number, dz: number, rot: number): Vec2 {
  const quart = ((rot % 4) + 4) % 4
  switch (quart) {
    case 1:
      return { x: dz, z: -dx }
    case 2:
      return { x: -dx, z: -dz }
    case 3:
      return { x: -dz, z: dx }
    default:
      return { x: dx, z: dz }
  }
}

/**
 * Rotation INVERSE: passe du repere du monde a celui du batiment.
 *
 * Indispensable et facile a rater: j'avais ecrit la collision des murs avec la
 * rotation directe, ce qui rendait les batiments tournes d'un ou de trois quarts
 * de tour impossibles a penetrer — leur porte se retrouvait du cote oppose.
 */
export function tournerInverse(dx: number, dz: number, rot: number): Vec2 {
  return tourner(dx, dz, 4 - (((rot % 4) + 4) % 4))
}

/** Les 4 coins de l'emprise, rotation appliquee, dans le sens horaire. */
export function empriseCoins(kind: BatimentKind, pos: Vec2, rot: number): Vec2[] {
  const t = BATIMENTS[kind].taille
  const hl = t.l / 2
  const hp = t.p / 2
  const locaux: [number, number][] = [
    [-hl, -hp],
    [hl, -hp],
    [hl, hp],
    [-hl, hp],
  ]
  return locaux.map(([dx, dz]) => {
    const v = tourner(dx, dz, rot)
    return { x: pos.x + v.x, z: pos.z + v.z }
  })
}

/**
 * Chevauchement de deux emprises. Les rotations etant des quarts de tour, les
 * deux rectangles restent alignes sur les axes du monde apres echange de l et p:
 * un test AABB est donc exact, pas une approximation.
 */
export function empriseChevauche(
  a: { kind: BatimentKind; pos: Vec2; rot: number },
  b: { kind: BatimentKind; pos: Vec2; rot: number },
  marge: number,
): boolean {
  const da = demiEmprise(a.kind, a.rot)
  const db = demiEmprise(b.kind, b.rot)
  const seuilX = da.hx + db.hx + marge
  const seuilZ = da.hz + db.hz + marge
  return Math.abs(a.pos.x - b.pos.x) < seuilX && Math.abs(a.pos.z - b.pos.z) < seuilZ
}

/** Vrai si (x, z) est dans l'emprise du batiment, elargie de `marge`. */
export function dansEmprise(
  b: { kind: BatimentKind; pos: Vec2; rot: number },
  x: number,
  z: number,
  marge = 0,
): boolean {
  const d = demiEmprise(b.kind, b.rot)
  return Math.abs(x - b.pos.x) <= d.hx + marge && Math.abs(z - b.pos.z) <= d.hz + marge
}

/**
 * Rayon du disque qui contient l'emprise, marge comprise. Sert aux appelants qui
 * eliminent d'abord par region avant d'appeler `dansEmprise` point par point.
 */
export function rayonEmprise(b: { kind: BatimentKind; rot: number }, marge = 0): number {
  const d = demiEmprise(b.kind, b.rot)
  return Math.hypot(d.hx + marge, d.hz + marge)
}

/**
 * Le batiment dont l'emprise recouvre ce point, sinon null.
 *
 * C'EST LA REPONSE UNIQUE a « y a-t-il une construction ici ? » pour tout ce
 * qui pousse. Rien ne doit pousser sous une maison: ni l'herbe et les fleurs du
 * decor (semees a partir de la seule graine, donc aveugles aux constructions),
 * ni l'arbre qu'on avait justement abattu pour faire de la place et qui revenait
 * trois jours plus tard au milieu du salon.
 *
 * On INTERROGE, on ne marque pas: rien n'est ecrit dans le noeud ni dans
 * l'accessoire, donc demolir suffit a tout faire revenir, et un batiment pose
 * par un autre joueur (arrive par le salon) compte des la seconde ou il entre
 * dans la liste.
 *
 * Balayage lineaire: les constructions se comptent par dizaines et le test est
 * un AABB EXACT (les rotations sont des quarts de tour). Un appelant qui teste
 * des dizaines de milliers de points doit d'abord eliminer par region — voir
 * `RenduProps.majCouverture`, qui se sert des spheres englobantes de ses lots.
 */
export function batimentCouvrant(
  batiments: readonly Batiment[],
  x: number,
  z: number,
  marge = 0,
): Batiment | null {
  for (const b of batiments) {
    if (couvreLePoint(b, x, z, marge)) return b
  }
  return null
}

/**
 * CE batiment couvre-t-il ce point ? Son emprise OU SA PISTE.
 *
 * UNE PISTE EST UNE CONSTRUCTION; elle n'est simplement pas une emprise (voir
 * `BatimentDef.piste`: une emprise porte des murs, un etage et une dalle, une
 * piste rien de tout cela). Elle n'en est pas moins du bitume pose par le
 * joueur, et l'oublier se voit a l'ecran: l'herbe du decor pousse AU TRAVERS du
 * tapis — une capture de bout en bout l'a montre — et l'arbre qu'on avait
 * justement abattu pour degager la piste y revient trois jours plus tard, en
 * travers du decollage. C'est exactement le probleme que cette fonction traite
 * pour les maisons, applique a la seule autre chose que le joueur pose au sol.
 *
 * La marge de la piste est celle de la POSE (`MARGE_PISTE_COUVERTURE`, l'etape
 * 3 de `validerPlacement`): on n'escamote que ce qui aurait empeche de batir —
 * « un noeud qui n'a jamais empeche de batir ne doit jamais disparaitre ».
 *
 * Un appelant qui elimine d'abord par region doit prendre `rayonCouverture`, et
 * non `rayonEmprise`: une aerogare traine vingt-cinq metres de piste derriere
 * elle, et un disque taille sur la seule emprise les laisse dehors.
 */
export function couvreLePoint(b: Batiment, x: number, z: number, marge = 0): boolean {
  if (dansEmprise(b, x, z, marge)) return true
  const piste = pisteDe(b)
  return piste !== null && surLaPiste(piste, x, z, Math.max(marge, MARGE_PISTE_COUVERTURE))
}

/** Marge d'une piste pour « quelque chose pousse-t-il ici ? »: celle de la pose. */
export const MARGE_PISTE_COUVERTURE = 0.5

/** Le disque qui contient tout ce que ce batiment couvre, PISTE COMPRISE. */
export function rayonCouverture(b: { kind: BatimentKind; rot: number }, marge = 0): number {
  const emprise = rayonEmprise(b, marge)
  if (!BATIMENTS[b.kind].piste) return emprise
  return Math.max(emprise, rayonVoisinage(b.kind) + marge)
}

/** Vrai si ce point est recouvert par une construction. */
export function couvertParBatiment(
  batiments: readonly Batiment[],
  x: number,
  z: number,
  marge = 0,
): boolean {
  return batimentCouvrant(batiments, x, z, marge) !== null
}

/**
 * Marge ajoutee a l'emprise pour ESCAMOTER LE DECOR.
 *
 * Les noeuds de ressource, eux, se testent a marge zero, exactement comme
 * l'etape 5 de `validerPlacement`: sinon on refuserait de rendre un arbre qui
 * n'a jamais empeche de batir. Le decor est plus permissif parce qu'une touffe
 * d'herbe posee pile sur l'arete deborde de sa moitie a l'interieur du mur.
 */
export const MARGE_COUVERTURE_DECOR = 0.12

/**
 * Marge negative d'un bassin, en metres. Meme role que celle du spa: sans elle,
 * on « nageait » debout sur la margelle.
 */
export const MARGE_BASSIN = 0.35

/** Les bassins de ce modele, ou rien. C'est la DONNEE qui dit ou l'on nage. */
export function bassinsDe(kind: BatimentKind): readonly BoiteFraction[] {
  return BATIMENTS[kind].bassins ?? AUCUN_BASSIN
}

const AUCUN_BASSIN: readonly BoiteFraction[] = Object.freeze([])

/**
 * Vrai si (x, z) tombe dans un bassin: on y nage comme en mer.
 *
 * ELLE DEMANDAIT `kind !== 'piscine'`, ET C'ETAIT LE PIEGE HABITUEL. « Une
 * regle ecrite en dur pour UN modele oublie toujours les suivants »: la
 * cueillette l'avait paye trois fois (le potager et la serre poussaient sans
 * rien donner), et le parc aquatique l'aurait paye une quatrieme — un grand
 * bassin dessine, plein d'eau, sur lequel on aurait marche. C'est la table
 * `bassins` du modele qui repond maintenant, et la piscine du catalogue y
 * declare une boite couvrant son emprise entiere: sa regle est mot pour mot
 * celle d'avant.
 *
 * Le nom de la fonction ne change pas, et c'est voulu: elle repond toujours a
 * « nage-t-on ici ? », et une seconde ecriture de cette question sous un autre
 * nom serait exactement ce que ce projet paie a chaque fois.
 */
export function dansPiscine(liste: readonly Batiment[], x: number, z: number): boolean {
  for (const b of liste) {
    const bassins = bassinsDe(b.kind)
    if (bassins.length === 0) continue
    // Repere local du batiment: on annule sa rotation, comme pour le spa.
    const local = tournerInverse(x - b.pos.x, z - b.pos.z, b.rot)
    for (const bassin of bassins) {
      const bo = boiteEtage(b.kind, bassin)
      if (
        Math.abs(local.x - bo.cx) <= bo.hx - MARGE_BASSIN &&
        Math.abs(local.z - bo.cz) <= bo.hz - MARGE_BASSIN
      ) {
        return true
      }
    }
  }
  return false
}

// ------------------------------------------------------------------- etages
//
// LE MOTEUR N'A AUCUNE VERTICALITE, et c'est le fait autour duquel tout ce qui
// suit est dessine. Un `Joueur` n'a que (x, z) — « la hauteur vient du
// terrain » —, le reseau ne porte pas de Y, la grille de navigation n'a qu'une
// seule couche, et un tap ne sait viser que le relief. Batir un vrai deuxieme
// plancher demanderait de reprendre les six.
//
// On ne le fait pas. Un etage est un NIVEAU: un entier a cote de la position,
// exactement comme `interieur` est un identifiant a cote de la position. Sur
// chaque niveau on marche en 2D, librement, sur toute l'emprise du batiment;
// ce qui change d'un niveau a l'autre, ce sont les MURS en vigueur et la
// hauteur a laquelle on dessine. On ne tombe pas du toit, et c'est assume.
//
// L'etage est la TERRASSE: on y marche partout, elle est fermee tout autour par
// un acrotere SANS OUVERTURE (une porte la-haut ferait marcher dans le vide),
// et elle porte deux choses — un bureau, qui a ses propres murs et sa propre
// porte, et un spa, ou l'on entre comme dans une piscine.

/**
 * Niveau le plus haut DU JEU ENTIER: 0 = le rez-de-chaussee, 1 = la terrasse
 * de la villa ou le premier etage de l'hotel, 2 = le dernier niveau de
 * l'hotel.
 *
 * C'est la borne que `decoderEntrees` applique a ce qu'un client raconte:
 * au-dela, il n'y a rien a atteindre. La borne PAR BATIMENT, elle, est
 * `niveauxDe`, et c'est `niveauEffectif` qui l'applique: se dire au niveau 2
 * sur une villa ne mene qu'au niveau 1.
 *
 * DERIVEE DU CATALOGUE, jamais ecrite a la main: une seconde copie du plus
 * haut `niveaux` aurait silencieusement plafonne le premier modele plus haut
 * que l'hotel — son dernier etage aurait ete injouable en multijoueur, et
 * seul un test ecrit a la main l'aurait dit.
 */
export const NIVEAU_MAX = Math.max(
  ...(Object.keys(BATIMENTS) as BatimentKind[]).map((k) => niveauxDe(k)),
)

/** Hauteur du plancher de la terrasse au-dessus du sol du batiment, en metres. */
export const HAUTEUR_ETAGE = 3.5

/** Epaisseur de l'acrotere qui borde la terrasse, en metres. */
export const EPAISSEUR_PARAPET = 0.28

/**
 * Marge negative du spa, en metres. Meme role que celle de `dansPiscine`: sans
 * elle on « barbotait » debout sur la margelle.
 */
export const MARGE_SPA = 0.35

/**
 * Le plan de la terrasse, en FRACTIONS de l'emprise.
 *
 * Meme forme et meme raison que `ANCRAGE_MEUBLE`: une seule table, une seule
 * rotation appliquee ensuite par `tourner`. Ecrire les quatre quarts de tour a
 * la main est ce qui avait deja mis la porte du mauvais cote.
 *
 * `dl` et `dp` sont des DEMI-dimensions, toujours en fractions. Les valeurs
 * sont choisies pour qu'aucune piece ne vienne mordre la bande de l'acrotere:
 * une piece a cheval sur les deux serait repoussee vers l'interieur par l'un et
 * vers l'exterieur par l'autre, un tick sur deux — la secousse permanente que
 * tout le reste du projet existe pour eviter. tests/unit/etage.test.ts le
 * verifie.
 */
export const PLAN_ETAGE = {
  /**
   * Debouche de l'escalier: contre le mur de gauche, a mi-profondeur. Il
   * arrive DANS la grande piece, et l'on ressort sur le pont par la porte —
   * c'est la circulation d'un vrai etage.
   */
  escalier: { x: -0.3, z: 0 },
  /**
   * LA GRANDE PIECE. Elle occupe presque toute la largeur du batiment et les
   * deux tiers de sa profondeur; sa porte est sur SA face -z, comme celle
   * d'une maison, et elle donne sur le pont.
   *
   * Elle a remplace un petit bureau de 2,8 x 3,5 m perdu dans un coin. Les
   * demi-dimensions sont bornees par la regle ecrite plus haut: la bande de
   * murs de la piece ne doit JAMAIS toucher celle de l'acrotere, sinon les
   * deux se renvoient le bonhomme un tick sur deux. Ici la piece pousse vers
   * l'exterieur jusqu'a |x| = 0.41 * 14 + PLAYER_RADIUS = 6,09 m quand
   * l'acrotere commence a pousser vers l'interieur a 6,37 m: il reste 28 cm de
   * jeu, et tests/unit/etage.test.ts balaie toute la terrasse pour le prouver.
   */
  piece: { x: 0, z: 0.173, dl: 0.41, dp: 0.227 },
  /** Le spa, sur le pont, devant la piece. On y entre: il n'a pas de murs. */
  spa: { x: 0.14, z: -0.25, dl: 0.13, dp: 0.13 },
  /** Le portable, sur le bureau, contre le mur du fond de la piece. */
  portable: { x: 0.26, z: 0.3 },
} as const

/**
 * Largeur de la porte-fenetre entre la grande piece et le pont, en metres.
 *
 * Plus large qu'une porte de maison: c'est une baie, on la traverse en
 * marchant droit, sans avoir a viser. Elle est ici et pas dans le rendu pour
 * la raison habituelle — le trou dessine et le trou de la collision doivent
 * etre le meme trou.
 */
export const LARGEUR_BAIE_ETAGE = 1.8

/** Vrai si ce modele porte au moins un etage ou l'on monte. */
export function aUnEtage(kind: BatimentKind): boolean {
  return BATIMENTS[kind].etage === true
}

/**
 * NOMBRE de niveaux au-dessus du sol de ce modele: 0 pour une maison
 * ordinaire, 1 pour la villa a etage, 2 pour l'hotel.
 *
 * C'est LA reponse a « jusqu'ou monte-t-on ici ? », et tout le monde la lit —
 * `niveauEffectif`, `resoudreMurs`, l'ascenseur, le rendu. Une seconde
 * ecriture de ce nombre serait le piege habituel des deux regles qui
 * divergent.
 */
export function niveauxDe(kind: BatimentKind): number {
  const def = BATIMENTS[kind]
  if (def.etage !== true) return 0
  return def.niveaux ?? 1
}

/**
 * LES PLANS D'ETAGE PAR MODELE. La villa garde son `PLAN_ETAGE` historique,
 * traduit ici dans la forme commune; l'hotel apporte le sien
 * (shared/data/hotel.ts). C'est la table que `murEtage` et les cibles
 * consultent — jamais le nom du modele.
 */
const PLANS_ETAGE: Partial<Record<BatimentKind, PlanModeleEtage>> = {
  villa_etage: {
    acces: 'escalier',
    accesPos: PLAN_ETAGE.escalier,
    niveaux: [[{ ...PLAN_ETAGE.piece, porte: LARGEUR_BAIE_ETAGE }]],
    portable: { ...PLAN_ETAGE.portable, niveau: 1 },
    spa: { ...PLAN_ETAGE.spa, porte: 0 },
  },
  hotel: PLAN_HOTEL,
  // LES TROIS AEROPORTS PARTAGENT LE MEME PLAN, litteralement le meme objet:
  // « fonctionnellement identiques » n'est pas une intention, c'est une seule
  // table lue trois fois.
  aerodrome: PLAN_AEROPORT,
  aeroport: PLAN_AEROPORT,
  aeroport_verre: PLAN_AEROPORT,
  // LA PASSERELLE DU PARC AQUATIQUE: un seul niveau, aucune piece, et des
  // GLISSADES qui redescendent dans le bassin (shared/data/parc-aquatique.ts).
  parc_aquatique: PLAN_PARC_AQUATIQUE,
}

/** Le plan d'etage de ce modele, ou null s'il n'en a pas. */
export function planEtageDe(kind: BatimentKind): PlanModeleEtage | null {
  return PLANS_ETAGE[kind] ?? null
}

/**
 * Comment on monte dans ce modele: l'escalier de la villa, l'ascenseur de
 * l'hotel, ou rien du tout.
 */
export function accesEtage(kind: BatimentKind): 'escalier' | 'ascenseur' | null {
  return planEtageDe(kind)?.acces ?? null
}

/**
 * Les pieces d'UN niveau de ce modele. Vide pour un niveau qui n'existe pas:
 * `murEtage` n'a alors que l'acrotere a resoudre.
 */
export function piecesEtage(kind: BatimentKind, niveau: number): readonly PieceEtage[] {
  const plan = planEtageDe(kind)
  if (!plan || niveau <= 0) return AUCUNE_PIECE
  return plan.niveaux[niveau - 1] ?? AUCUNE_PIECE
}

const AUCUNE_PIECE: readonly PieceEtage[] = Object.freeze([])

/**
 * Une piece, en repere LOCAL du batiment (rotation non appliquee).
 *
 * Sert a la terrasse de la villa comme aux enclos du zoo: c'est la meme
 * question — « ou est cette piece dans son batiment ? » — et il n'y avait
 * aucune raison de l'ecrire deux fois.
 */
export interface BoiteEtage {
  cx: number
  cz: number
  /** Demi-dimensions, en metres. */
  hx: number
  hz: number
}

/** Passe une piece du plan (en fractions) au repere local, en metres. */
export function boiteEtage(
  kind: BatimentKind,
  piece: { x: number; z: number; dl: number; dp: number },
): BoiteEtage {
  const t = BATIMENTS[kind].taille
  return { cx: t.l * piece.x, cz: t.p * piece.z, hx: t.l * piece.dl, hz: t.p * piece.dp }
}

/**
 * Position monde d'un point du plan d'un modele, rotation comprise.
 *
 * UNE SEULE fonction pour la question « ou est ce point du plan dans le
 * monde ? », qu'il s'agisse du debouche d'un escalier, du portable d'un bureau,
 * du comptoir d'un restaurant, d'une fontaine du parc ou du haut d'une
 * glissade. Une seconde ecriture du quart de tour est ce qui avait deja mis une
 * porte du mauvais cote.
 */
export function pointDuPlan(b: Batiment, ancre: { x: number; z: number }): Vec2 {
  const t = BATIMENTS[b.kind].taille
  const v = tourner(t.l * ancre.x, t.p * ancre.z, b.rot)
  return { x: b.pos.x + v.x, z: b.pos.z + v.z }
}

/**
 * Position monde de l'ACCES aux etages — escalier ou ascenseur, c'est la meme
 * colonne a tous les niveaux. Repli sur le plan de la villa pour un modele
 * sans plan: la question ne devrait pas lui etre posee.
 */
export function positionAcces(b: Batiment): Vec2 {
  return pointDuPlan(b, planEtageDe(b.kind)?.accesPos ?? PLAN_ETAGE.escalier)
}

/** Position monde du debouche de l'escalier. Le MEME point aux deux niveaux. */
export function positionEscalier(b: Batiment): Vec2 {
  return positionAcces(b)
}

/** Position monde de la cabine d'ascenseur. La MEME colonne a tous les niveaux. */
export function positionAscenseur(b: Batiment): Vec2 {
  return positionAcces(b)
}

/** Position monde du portable pose sur le bureau, selon le plan du modele. */
export function positionPortable(b: Batiment): Vec2 {
  return pointDuPlan(b, planEtageDe(b.kind)?.portable ?? PLAN_ETAGE.portable)
}

/** Position monde du centre du spa. */
export function positionSpa(b: Batiment): Vec2 {
  return pointDuPlan(b, PLAN_ETAGE.spa)
}

/** Position monde du centre de la grande piece de l'etage. */
export function positionPiece(b: Batiment): Vec2 {
  return pointDuPlan(b, PLAN_ETAGE.piece)
}

/** Position monde du comptoir du restaurant, selon le plan du modele. */
export function positionRestaurant(b: Batiment): Vec2 {
  const r = planEtageDe(b.kind)?.restaurant
  return r ? pointDuPlan(b, r) : b.pos
}

// ---------------------------------------------------------------- glissades
//
// DESCENDRE UNE GLISSADE EST UN TRAJET, PAS UNE TELEPORTATION, et c'est la
// seule conception qui tienne dans ce moteur. Le serveur fait autorite sur les
// positions (shared/sim/entree.ts): un client qui poserait le bonhomme au bas
// du toboggan recevrait un `recalage` et le verrait remonter d'un coup — le
// symptome exact du bogue de l'arbre coupe, paye en aout. Une glissade se
// descend donc par le PAS PARTAGE, comme on marche:
//
//   1. On touche le haut de la glissade, sur la passerelle (`glissadeAuPoint`
//      donne la cible; « une action qui n'existe qu'au clavier n'existe pas »,
//      donc le geste est un TAP sur un toboggan qu'on voit).
//   2. Le client emet des entrees ordinaires le long du chemin
//      (`cheminGlissade`), a `VITESSE_GLISSADE` — plafonnee comme le reste par
//      `decoderEntrees`, qui ne connait rien aux glissades et n'a rien a
//      apprendre.
//   3. On reste au niveau du HAUT pendant toute la descente, et l'on ne
//      redescend au niveau du bassin qu'a l'arrivee (`avancerGlissade`).
//      Changer de niveau a mi-course reviendrait a resoudre le pas contre les
//      murs du rez-de-chaussee alors qu'on est encore en l'air.
//
// Le serveur rejoue exactement les memes entrees: rien de neuf sur le fil, rien
// de neuf a tricher. Un joueur fatigue glisse un peu moins vite, parce que la
// descente passe par le pas ordinaire et ses modificateurs — c'est le prix, et
// il est juste.
//
// La HAUTEUR du bonhomme pendant la descente ne regarde que le rendu: la
// simulation ne connait que deux niveaux entiers, et le client est libre
// d'interpoler entre les deux tant qu'il ne touche pas a (x, z).

/**
 * Vitesse de descente d'une glissade, en m/s.
 *
 * Sous `RUN_SPEED` (7 m/s) et pas par hasard: c'est la borne que
 * `decoderEntrees` applique deja a toute entree. Une glissade plus rapide que
 * la course aurait demande une exception dans l'autorite du deplacement, et une
 * exception dans l'autorite du deplacement est un trou.
 */
export const VITESSE_GLISSADE = 6

/** Les glissades de ce modele, ou rien. C'est le PLAN qui repond. */
export function glissadesDe(kind: BatimentKind): readonly Glissade[] {
  return planEtageDe(kind)?.glissades ?? AUCUNE_GLISSADE
}

const AUCUNE_GLISSADE: readonly Glissade[] = Object.freeze([])

/** Le chemin d'une glissade, en coordonnees du MONDE. */
export interface CheminGlissade {
  /** Le haut, sur la passerelle. */
  depart: Vec2
  /** Le bas, dans le bassin. */
  arrivee: Vec2
  /** Vecteur unitaire du haut vers le bas. */
  sens: Vec2
  /** Longueur au sol, en metres (le moteur n'a pas de hauteur). */
  longueur: number
  /** Niveau ou l'on se tient au depart, et celui ou l'on arrive. */
  niveauHaut: number
  niveauBas: number
}

/**
 * Le chemin de la `index`-ieme glissade de ce batiment, ou null.
 *
 * `niveauHaut` est le dernier niveau du modele: les departs sont sur la
 * passerelle la plus haute. C'est la donnee qui le dit (`niveauxDe`), jamais un
 * 1 ecrit en dur — le jour ou un parc aura deux passerelles, il n'y aura rien a
 * retrouver ici.
 */
export function cheminGlissade(b: Batiment, index: number): CheminGlissade | null {
  const g = glissadesDe(b.kind)[index]
  if (!g) return null
  const depart = pointDuPlan(b, g.depart)
  const arrivee = pointDuPlan(b, g.arrivee)
  const dx = arrivee.x - depart.x
  const dz = arrivee.z - depart.z
  const longueur = Math.hypot(dx, dz)
  // Une glissade de longueur nulle n'existe pas: le plan serait faux, et le
  // sens serait un NaN qui se propagerait dans la position du joueur.
  const sens = longueur > 1e-6 ? { x: dx / longueur, z: dz / longueur } : { x: 0, z: 1 }
  return { depart, arrivee, sens, longueur, niveauHaut: niveauxDe(b.kind), niveauBas: 0 }
}

/**
 * OU L'ON EN EST d'une descente, apres `parcouru` metres.
 *
 * Fonction PURE, et c'est tout l'interet: le client s'en sert pour viser son
 * entree du tick, et le test s'en sert pour rejouer une descente entiere sans
 * ouvrir une page. Le niveau ne tombe qu'a l'ARRIVEE — voir plus haut.
 */
export function avancerGlissade(
  chemin: CheminGlissade,
  parcouru: number,
): { pos: Vec2; niveau: number; fini: boolean } {
  // Un `parcouru` qui n'est pas un nombre viendrait d'un bogue d'appelant, pas
  // d'un joueur: on le ramene au depart plutot que de propager un NaN dans la
  // position du bonhomme.
  const d = Number.isFinite(parcouru) ? clamp(parcouru, 0, chemin.longueur) : 0
  const fini = d >= chemin.longueur
  return {
    pos: fini
      ? { x: chemin.arrivee.x, z: chemin.arrivee.z }
      : { x: chemin.depart.x + chemin.sens.x * d, z: chemin.depart.z + chemin.sens.z * d },
    niveau: fini ? chemin.niveauBas : chemin.niveauHaut,
    fini,
  }
}

/**
 * L'index de la glissade dont le DEPART est a portee de ce point, ou -1.
 *
 * Le niveau compte: proposer « Glisser » depuis le bord du bassin, trois metres
 * plus bas, serait le mensonge deja paye par « Couper — Arbre » annonce a
 * quelqu'un sur son toit. On ne repond donc que depuis la passerelle.
 */
export function glissadeAuPoint(
  b: Batiment,
  x: number,
  z: number,
  niveau: number,
  portee: number,
): number {
  const glissades = glissadesDe(b.kind)
  if (glissades.length === 0 || niveau !== niveauxDe(b.kind)) return -1
  let meilleur = -1
  let meilleureD = portee
  for (let i = 0; i < glissades.length; i++) {
    const g = glissades[i]
    if (!g) continue
    const p = pointDuPlan(b, g.depart)
    const d = Math.hypot(p.x - x, p.z - z)
    if (d < meilleureD) {
      meilleureD = d
      meilleur = i
    }
  }
  return meilleur
}

// -------------------------------------------------------------------- piste
//
// LA PISTE D'UN AEROPORT vit ici, avec le reste de la geometrie des batiments,
// et pas dans un module a elle: c'est une piece du MODELE (`BatimentDef.piste`),
// elle se tourne avec lui par `tourner`, et la recopier ailleurs aurait fait
// exactement ce que ce projet paie a chaque fois — deux ecritures d'un quart de
// tour qui finissent par ne plus etre d'accord.

/** Tout ce qu'il faut savoir d'une piste, en coordonnees du MONDE. */
export interface Piste {
  /** Cap de DECOLLAGE, en radians (0 = vers +z): on s'eloigne de l'aerogare. */
  cap: number
  /** Vecteur unitaire de ce cap. */
  sens: Vec2
  /** Seuil cote aerogare: le tablier, la ou l'on se gare et d'ou l'on part. */
  seuilProche: Vec2
  /** Seuil oppose: la ou l'on se pose, et ou l'on quitte le sol. */
  seuilLoin: Vec2
  centre: Vec2
  longueur: number
  largeur: number
  /** Point de stationnement sur l'axe, nez dans le sens du decollage. */
  parking: Vec2
}

/** Distance du seuil proche a laquelle un appareil se gare, en metres. */
export const RECUL_PARKING = 5

/**
 * LA PISTE de ce batiment, ou null s'il n'en a pas.
 *
 * Elle part de la face -z (celle de la porte, la convention du projet) et
 * s'eloigne: on decolle donc en tournant le dos a l'aerogare, et l'on atterrit
 * dans l'autre sens, en roulant vers elle. Se poser en s'eloignant obligerait a
 * un demi-tour sur place, et decoller vers le batiment serait une facon comme
 * une autre d'entrer dans le hall.
 */
export function pisteDe(b: { kind: BatimentKind; pos: Vec2; rot: number }): Piste | null {
  const def = BATIMENTS[b.kind]
  const p = def.piste
  if (!p) return null
  const t = def.taille
  const sens = tourner(0, -1, b.rot)
  const depart = tourner(0, -t.p / 2, b.rot)
  const seuilProche = { x: b.pos.x + depart.x + sens.x * p.ecart, z: b.pos.z + depart.z + sens.z * p.ecart }
  const seuilLoin = { x: seuilProche.x + sens.x * p.longueur, z: seuilProche.z + sens.z * p.longueur }
  return {
    cap: Math.atan2(sens.x, sens.z),
    sens,
    seuilProche,
    seuilLoin,
    centre: { x: (seuilProche.x + seuilLoin.x) / 2, z: (seuilProche.z + seuilLoin.z) / 2 },
    longueur: p.longueur,
    largeur: p.largeur,
    parking: { x: seuilProche.x + sens.x * RECUL_PARKING, z: seuilProche.z + sens.z * RECUL_PARKING },
  }
}

/** Vrai si ce modele porte une piste. C'est la DONNEE qui repond, pas un nom. */
export function estAeroport(kind: BatimentKind): boolean {
  return BATIMENTS[kind].piste !== undefined
}

/**
 * JUSQU'OU CHERCHER LES NOEUDS DE RESSOURCE avant de poser ce modele.
 *
 * POURQUOI CETTE FONCTION EXISTE — et c'est une histoire de quatorze metres.
 * Le client n'envoyait a `validerPlacement` que les noeuds situes dans un rayon
 * ECRIT EN DUR (14 m, « large devant la villa 12 x 9 »). Le serveur, lui,
 * rejoue la meme validation avec TOUS les noeuds du monde. Tant qu'aucun modele
 * ne depassait quatorze metres, les deux tombaient d'accord.
 *
 * Une aerogare traine vingt-cinq metres de piste derriere elle: le client ne
 * voyait donc pas le palmier plante a vingt metres du seuil, acceptait la pose,
 * et le serveur la refusait une seconde plus tard — le batiment apparaissait
 * puis disparaissait, sans que rien n'explique pourquoi. Une campagne de bout
 * en bout l'a pris sur le fait.
 *
 * Le rayon est desormais une propriete du MODELE, comme tout le reste: emprise,
 * piste comprise, plus une marge. Deux regles pour la meme question finissent
 * toujours par diverger — il n'y en a plus qu'une.
 */
export function rayonVoisinage(kind: BatimentKind): number {
  const def = BATIMENTS[kind]
  const emprise = Math.hypot(def.taille.l, def.taille.p) / 2
  const p = def.piste
  if (!p) return emprise + 2
  // Le point le plus lointain de la piste: le coin du seuil oppose.
  return Math.hypot(p.largeur / 2, def.taille.p / 2 + p.ecart + p.longueur) + 2
}

/**
 * Coordonnees d'un point DANS LE REPERE DE LA PISTE: `long` le long de l'axe
 * depuis le seuil proche (positif en s'eloignant de l'aerogare), `lat` en
 * travers.
 *
 * C'est la conversion dont vit tout le pilotage: « suis-je aligne ? » devient
 * « |lat| est-il petit ? », et « ou en suis-je de ma course ? » devient « ou en
 * est `long` ? ».
 */
export function reperePiste(piste: Piste, x: number, z: number): { long: number; lat: number } {
  const dx = x - piste.seuilProche.x
  const dz = z - piste.seuilProche.z
  return {
    long: dx * piste.sens.x + dz * piste.sens.z,
    lat: dx * piste.sens.z - dz * piste.sens.x,
  }
}

/** Le point du monde correspondant. L'inverse EXACT de `reperePiste`. */
export function pointPiste(piste: Piste, long: number, lat: number): Vec2 {
  return {
    x: piste.seuilProche.x + piste.sens.x * long + piste.sens.z * lat,
    z: piste.seuilProche.z + piste.sens.z * long - piste.sens.x * lat,
  }
}

/** Vrai si (x, z) est sur le ruban, elargi de `marge`. */
export function surLaPiste(piste: Piste, x: number, z: number, marge = 0): boolean {
  const r = reperePiste(piste, x, z)
  return r.long >= -marge && r.long <= piste.longueur + marge && Math.abs(r.lat) <= piste.largeur / 2 + marge
}

/**
 * Points de sondage du ruban, pour juger le terrain avant de batir.
 *
 * Un quadrillage, pas quatre coins: une bosse de deux metres au milieu d'une
 * piste de trente passerait entre les coins sans etre vue, et l'avion roulerait
 * dedans.
 */
export function pointsPiste(piste: Piste, pas = 2.5): Vec2[] {
  const points: Vec2[] = []
  const nLong = Math.max(2, Math.round(piste.longueur / pas))
  const nLat = Math.max(2, Math.round(piste.largeur / pas))
  for (let i = 0; i <= nLong; i++) {
    for (let j = 0; j <= nLat; j++) {
      points.push(pointPiste(piste, (i / nLong) * piste.longueur, (j / nLat - 0.5) * piste.largeur))
    }
  }
  return points
}

/**
 * LE QUADRILLAGE DU RUBAN DE BITUME, et pourquoi il vit ici.
 *
 * Le ruban est dessine par le client, mais la question a laquelle il repond —
 * « a quelle hauteur poser le tapis pour qu'il COUVRE le sol ? » — est de la
 * geometrie pure, et c'est la seule facon de la verrouiller par un test: on ne
 * juge pas une couverture a l'oeil sur une capture, on la MESURE.
 *
 * DEUX FOIS LA MEME ERREUR DE RAISONNEMENT avaient laisse le sable ressortir a
 * travers l'asphalte: un sommet ne dit la hauteur du sol qu'EN UN POINT. Il n'y
 * en avait que sur les deux bords, donc six metres de largeur passaient sous
 * une piste bombee; et une bosse entre deux tranches passait au-dessus de la
 * corde qui les relie. Mesure sur soixante emplacements valides du vrai monde:
 * le terrain ressortait sur 18 pistes, jusqu'a 20 cm.
 *
 * D'ou les deux decisions de cette fonction:
 *   - on quadrille DANS LES DEUX SENS (`COLONNES_RUBAN`), pas seulement en
 *     long;
 *   - chaque sommet prend le **MAXIMUM du sol dans sa maille**, parce qu'une
 *     surface lineaire tendue sur des maxima passe au-dessus de tout ce qu'elle
 *     enjambe. C'est le raisonnement de `pointsPiste`, qui sonde un
 *     quadrillage et non quatre coins.
 *
 * Le repere: `i` va du seuil PROCHE (le tablier) vers le seuil lointain, `j`
 * va de `lat = +largeur/2` vers `lat = -largeur/2`. Le rendu travaille dans le
 * repere local du batiment, ou `x = -lat` (verifie: a rot 0, `sens` vaut
 * (0, -1), donc `lat = -x`): son `j` croissant depuis `x = -demi` est donc
 * exactement le notre. Les hauteurs sont en MONDE — au rendu de soustraire
 * `b.y`.
 */
export const TRANCHES_RUBAN = 22
export const COLONNES_RUBAN = 4

/** Epaisseur du tapis au-dessus du sol le plus haut de sa maille, en metres. */
export const EPAISSEUR_RUBAN = 0.06

export function hauteursRuban(piste: Piste, terrain: ITerrain): number[][] {
  const pasLong = piste.longueur / TRANCHES_RUBAN
  const pasLat = piste.largeur / COLONNES_RUBAN
  const grille: number[][] = []
  for (let i = 0; i <= TRANCHES_RUBAN; i++) {
    const ligne: number[] = []
    for (let j = 0; j <= COLONNES_RUBAN; j++) {
      // Le maximum sur les neuf points de la demi-maille: c'est ce qui garantit
      // que la surface tendue entre les sommets ne plonge jamais sous le sol.
      let h = -Infinity
      for (let a = -1; a <= 1; a++) {
        for (let b = -1; b <= 1; b++) {
          const p = pointPiste(piste, i * pasLong + (a * pasLong) / 2, piste.largeur / 2 - j * pasLat + (b * pasLat) / 2)
          const v = terrain.hauteur(p.x, p.z)
          if (v > h) h = v
        }
      }
      ligne.push(h + EPAISSEUR_RUBAN)
    }
    grille.push(ligne)
  }
  return grille
}

/**
 * LE TERRAIN SOUS LA PISTE, verifie comme le reste — et refuse en le DISANT.
 *
 * Meme forme que l'etape 2 de `validerPlacement`, avec une difference assumee:
 * on tolere plus de denivele (`piste.deniveleMax`), parce que le tapis SUIT le
 * sol au lieu de le raser. Un joueur dont l'aerogare passerait et dont la piste
 * ne passe pas doit savoir laquelle des deux le refuse: c'est tout l'interet
 * d'une phrase separee.
 */
export function pisteDegagee(
  kind: BatimentKind,
  pos: Vec2,
  rot: number,
  terrain: ITerrain,
): ValidationPlacement {
  const def = BATIMENTS[kind]
  const piste = def.piste ? pisteDe({ kind, pos, rot }) : null
  if (!def.piste || !piste) return { ok: true }
  let somme = 0
  let penteMax = 0
  let hMin = Infinity
  let hMax = -Infinity
  const points = pointsPiste(piste)
  for (const p of points) {
    if (p.x < 0 || p.x > CARTE_X || p.z < 0 || p.z > CARTE_Z) {
      return { ok: false, raison: 'La piste sortirait du monde' }
    }
    if (terrain.estEau(p.x, p.z)) return { ok: false, raison: 'La piste tomberait à l’eau' }
    const ech = terrain.echantillon(p.x, p.z)
    somme += ech.pente
    if (ech.pente > penteMax) penteMax = ech.pente
    if (ech.hauteur < hMin) hMin = ech.hauteur
    if (ech.hauteur > hMax) hMax = ech.hauteur
  }
  if (somme / points.length > PENTE_MOYENNE_MAX || penteMax > PENTE_PONCTUELLE_MAX) {
    return { ok: false, raison: 'Il faut du plat devant : la piste est trop en pente' }
  }
  if (hMax - hMin > def.piste.deniveleMax) {
    return { ok: false, raison: 'Il faut du plat devant : la piste monte et descend trop' }
  }
  return { ok: true }
}

/**
 * Le batiment a etage dont l'emprise recouvre ce point, sinon null.
 *
 * `marge` est GENEREUSE a dessein quand on s'en sert pour decider d'un niveau:
 * l'acrotere tient deja le joueur a plus d'un metre du bord, donc la reponse
 * n'est jamais rendue au ras de sa propre frontiere. C'est ce qui permet au
 * navigateur et au serveur de tomber sur le meme niveau alors que leurs
 * positions different de quelques centimetres — s'ils n'etaient pas d'accord,
 * ils ne resoudraient pas les memes murs, et le bonhomme tremblerait.
 */
export function batimentEtageContenant(
  liste: readonly Batiment[],
  x: number,
  z: number,
  marge = 0,
): Batiment | null {
  for (const b of liste) {
    if (!aUnEtage(b.kind)) continue
    if (dansEmprise(b, x, z, marge)) return b
  }
  return null
}

/**
 * LE NIVEAU REELLEMENT TENABLE a cet endroit.
 *
 * Le niveau voulu vient du client (voir `Entree.niv`), donc il se verifie — et
 * il se verifie ICI, dans du code pur appele par les deux cotes avec les memes
 * nombres. Hors d'un batiment a etage il n'y a pas d'etage: on retombe au sol.
 * Un client qui mentirait ne gagnerait que le droit de se tenir sur SON propre
 * toit sans avoir pris l'escalier.
 */
export function niveauEffectif(liste: readonly Batiment[], x: number, z: number, niveau: number): number {
  if (!Number.isFinite(niveau) || niveau <= 0) return 0
  const b = batimentEtageContenant(liste, x, z, PLAYER_RADIUS)
  if (b === null) return 0
  // La borne du BATIMENT, pas celle du jeu: se dire au niveau 2 sur une villa
  // ne mene qu'a sa terrasse.
  return Math.min(Math.floor(niveau), niveauxDe(b.kind))
}

/**
 * Hauteur du sol sous les pieds du joueur, ou null s'il est au rez-de-chaussee.
 *
 * Une seule fonction pour la question, comme `positionPossible` pour « peut-il
 * etre ici ? »: le bonhomme, les autres joueurs et la camera doivent monter
 * exactement de la meme hauteur, sinon quelqu'un a l'etage apparait debout sur
 * la pelouse.
 */
export function hauteurNiveau(
  liste: readonly Batiment[],
  x: number,
  z: number,
  niveau: number,
): number | null {
  if (niveau <= 0) return null
  const b = batimentEtageContenant(liste, x, z, PLAYER_RADIUS)
  if (b === null) return null
  // Chaque niveau monte d'une dalle. La borne du batiment s'applique ici
  // aussi: un niveau que ce modele n'a pas ne doit pas inventer une hauteur.
  return b.y + HAUTEUR_ETAGE * Math.min(Math.max(1, Math.floor(niveau)), niveauxDe(b.kind))
}

/**
 * Vrai si (x, z) tombe dans le spa de la terrasse.
 *
 * Copie de `dansPiscine`, marge negative comprise, mais avec une difference qui
 * compte: le spa ne fait PAS nager. `Joueur.nage` est l'etat de la mer et de la
 * piscine — il enfonce le bonhomme, le couche vers l'avant et lui donne la
 * brasse. Dans un bain a remous, ce serait absurde. On s'y assoit, et l'energie
 * remonte doucement (voir `reposerAuSpa` dans ./besoins.ts).
 */
export function dansSpa(liste: readonly Batiment[], x: number, z: number, niveau: number): boolean {
  if (niveau <= 0) return false
  for (const b of liste) {
    // C'est le PLAN qui dit s'il y a un spa: l'hotel a un etage mais pas de
    // spa — son bassin est aux poissons, on ne s'y trempe pas.
    const spa = planEtageDe(b.kind)?.spa
    if (!spa) continue
    const local = tournerInverse(x - b.pos.x, z - b.pos.z, b.rot)
    const boite = boiteEtage(b.kind, spa)
    if (
      Math.abs(local.x - boite.cx) <= boite.hx - MARGE_SPA &&
      Math.abs(local.z - boite.cz) <= boite.hz - MARGE_SPA
    ) {
      return true
    }
  }
  return false
}

/**
 * Vrai si cette construction doit BARRER la grille de navigation.
 *
 * Regle unique, parce que le serveur construit desormais sa propre grille pour
 * faire autorite sur les deplacements: si sa grille ne bloquait pas exactement
 * les memes emprises que celle du navigateur, chaque cloture deviendrait un
 * endroit ou le client et le serveur ne sont pas d'accord, c'est-a-dire une
 * secousse permanente.
 *
 * La piscine se nage, le jardin se traverse, et LE PORTAIL NE BLOQUE PAS: c'est
 * une porte. Sans cette exception, boucler une cloture autour de soi revenait a
 * se murer vivant. Les maisons, elles, ne sont pas dans la grille du tout —
 * on y entre, et `resoudreMurs` s'occupe de leurs murs.
 */
export function barreLePassage(b: Batiment): boolean {
  if (BATIMENTS[b.kind].habitable) return false
  // Un bassin ne barre pas: on le TRAVERSE a la nage. C'est la donnee qui le
  // dit (`bassins`), comme pour `dansPiscine` — sans quoi la piscine serait
  // restee le seul modele d'eau que la navigation sait laisser passer.
  return bassinsDe(b.kind).length === 0 && b.kind !== 'jardin' && b.kind !== 'portail'
}

/** Points d'echantillonnage du sol: centre, coins et milieux des cotes. */
function pointsSondage(kind: BatimentKind, pos: Vec2, rot: number): Vec2[] {
  const coins = empriseCoins(kind, pos, rot)
  const points: Vec2[] = [pos, ...coins]
  for (let i = 0; i < coins.length; i++) {
    const a = coins[i] as Vec2
    const b = coins[(i + 1) % coins.length] as Vec2
    points.push({ x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 })
  }
  return points
}

// ------------------------------------------------------------------ validation

/** "Il faut 20 bois et 4 pierre" a partir de ce qui manque. */
function phraseManquant(trou: Cout): string {
  const morceaux: string[] = []
  for (const cle of Object.keys(trou) as ItemId[]) {
    const n = trou[cle] ?? 0
    if (n > 0) morceaux.push(`${n} ${OBJETS[cle].nom.toLowerCase()}`)
  }
  if (morceaux.length === 0) return 'Il manque des ressources'
  if (morceaux.length === 1) return `Il faut ${morceaux[0]}`
  const dernier = morceaux.pop() as string
  return `Il faut ${morceaux.join(', ')} et ${dernier}`
}

export function validerPlacement(
  kind: BatimentKind,
  pos: Vec2,
  rot: number,
  terrain: ITerrain,
  batiments: Batiment[],
  inventaire: Inventaire,
  ressources?: Ressource[],
  posJoueur?: Vec2,
): ValidationPlacement {
  // On ne batit pas sur quelqu'un. Sans ce garde-fou, poser un amenagement sur
  // sa propre position enfermait le bonhomme dans une zone bloquee de tous les
  // cotes, et il ne pouvait plus bouger du tout.
  if (posJoueur && dansEmprise({ kind, pos, rot }, posJoueur.x, posJoueur.z, PLAYER_RADIUS + 0.25)) {
    return { ok: false, raison: 'Tu es à cet endroit : écarte-toi d’abord' }
  }
  const def = BATIMENTS[kind]
  const demi = demiEmprise(kind, rot)

  // 1. Dans le monde, emprise comprise.
  if (
    pos.x - demi.hx < 0 ||
    pos.x + demi.hx > CARTE_X ||
    pos.z - demi.hz < 0 ||
    pos.z + demi.hz > CARTE_Z
  ) {
    return { ok: false, raison: 'Hors du monde' }
  }

  // 2. Pas dans l'eau, et sol assez plat.
  const points = pointsSondage(kind, pos, rot)
  let sommePente = 0
  let penteMax = 0
  let hMin = Infinity
  let hMax = -Infinity
  for (const p of points) {
    if (terrain.estEau(p.x, p.z)) return { ok: false, raison: "Dans l'eau" }
    const ech = terrain.echantillon(p.x, p.z)
    sommePente += ech.pente
    if (ech.pente > penteMax) penteMax = ech.pente
    if (ech.hauteur < hMin) hMin = ech.hauteur
    if (ech.hauteur > hMax) hMax = ech.hauteur
  }
  const surface = def.taille.l * def.taille.p
  const tolerance = surface < SURFACE_PETITE ? 1.8 : 1
  const penteMoyenne = sommePente / points.length
  if (
    penteMoyenne > PENTE_MOYENNE_MAX * tolerance ||
    penteMax > PENTE_PONCTUELLE_MAX * tolerance ||
    hMax - hMin > DENIVELE_MAX * tolerance
  ) {
    return { ok: false, raison: 'Terrain trop en pente' }
  }

  // 3. Pas de chevauchement avec l'existant.
  for (const autre of batiments) {
    if (empriseChevauche({ kind, pos, rot }, autre, MARGE_BATIMENT)) {
      return { ok: false, raison: "Trop près d'un autre bâtiment" }
    }
  }

  /*
   * 3 bis. LA PISTE, pour les modeles qui en ont une.
   *
   * Elle n'est pas dans l'emprise (voir `BatimentDef.piste`), donc rien de ce
   * qui precede ne l'a regardee: sans ce test, on poserait une aerogare sur un
   * beau replat avec sa piste plantee dans un lac ou grimpant une falaise. On
   * verifie le sol ET ce qui traine dessus, exactement comme pour l'emprise.
   */
  const piste = pisteDe({ kind, pos, rot })
  if (piste) {
    const sol = pisteDegagee(kind, pos, rot, terrain)
    if (!sol.ok) return sol
    for (const autre of batiments) {
      const t = BATIMENTS[autre.kind].taille
      const rayon = Math.hypot(t.l, t.p) / 2
      if (surLaPiste(piste, autre.pos.x, autre.pos.z, rayon + MARGE_BATIMENT)) {
        return { ok: false, raison: 'Un bâtiment est en travers de la piste' }
      }
    }
    if (ressources) {
      for (const r of ressources) {
        if (r.pv <= 0) continue
        if (!surLaPiste(piste, r.pos.x, r.pos.z, 0.5)) continue
        return {
          ok: false,
          raison: `La piste est encombrée (${RESSOURCES[r.kind].nom.toLowerCase()}) : dégage-la d’abord.`,
        }
      }
    }
  }

  // 4. De quoi payer.
  if (!peutPayer(inventaire, def.cout)) {
    return { ok: false, raison: phraseManquant(manquant(inventaire, def.cout)) }
  }

  /*
   * 5. Pas sur un noeud de ressource — ET ON DIT LESQUELS.
   *
   * « Emplacement occupé, il faut récolter avant » etait vrai et inutilisable:
   * sous une grande emprise, on ne voit pas ce qui gene et on deplace le
   * fantome au hasard jusqu'a ce que ca passe. Un balayage du monde a mesure
   * ZERO emplacement de zoo en foret — parce que la foret est pleine d'arbres,
   * ce qu'un joueur peut parfaitement regler A LA HACHE, encore faut-il qu'on
   * le lui dise. On compte donc, et on nomme.
   *
   * On ne s'arrete PAS au premier: savoir qu'il reste un arbre ou qu'il en
   * reste onze ne demande pas le meme geste.
   */
  if (ressources) {
    let genants = 0
    let premier: RessourceKind | null = null
    for (const r of ressources) {
      if (r.pv <= 0) continue
      if (!dansEmprise({ kind, pos, rot }, r.pos.x, r.pos.z)) continue
      genants++
      if (premier === null) premier = r.kind
    }
    if (genants > 0 && premier !== null) {
      const nom = RESSOURCES[premier].nom.toLowerCase()
      const quoi = genants === 1 ? `1 ${nom}` : `${genants} ${nom}s et autres`
      return { ok: false, raison: `Emplacement occupé (${quoi}) : récolte d’abord ce qui est dessous.` }
    }
  }

  return { ok: true }
}

// ------------------------------------------------------------------ creation

/**
 * Identifiant stable et sans horloge: hachage de kind + position + rotation +
 * proprietaire. Deux batiments ne peuvent pas partager le meme centre (le test
 * de chevauchement l'interdit), donc la collision est impossible en pratique, et
 * l'avantage est enorme: le client et le serveur calculent le MEME id pour la
 * meme pose, sans se parler ni tenir un compteur partage.
 */
export function idBatiment(kind: BatimentKind, pos: Vec2, rot: number, proprietaire: string): string {
  const gx = Math.round(pos.x * 100)
  const gz = Math.round(pos.z * 100)
  let graine = 0x9e3779b9
  for (let i = 0; i < proprietaire.length; i++) {
    graine = (Math.imul(graine ^ proprietaire.charCodeAt(i), 16777619) >>> 0) >>> 0
  }
  for (let i = 0; i < kind.length; i++) {
    graine = (Math.imul(graine ^ kind.charCodeAt(i), 16777619) >>> 0) >>> 0
  }
  const h1 = Math.floor(hache2(gx, gz, graine) * 0xffffffff) >>> 0
  const h2 = Math.floor(hache2(gz + rot * 7919, gx, graine ^ 0x51ed270b) * 0xffffffff) >>> 0
  return `b_${h1.toString(36)}${h2.toString(36)}`
}

/**
 * Part des ressources rendue quand on demolit sa propre construction.
 *
 * La moitie: assez pour corriger une erreur de placement sans regret, pas assez
 * pour que batir et demolir en boucle devienne une facon de ranger son
 * inventaire.
 */
export const PART_REMBOURSEE = 0.5

/**
 * Ce que rend la demolition d'un batiment. Arrondi vers le BAS: on ne gagne
 * jamais de matiere en demolissant.
 */
export function remboursement(kind: BatimentKind): Cout {
  const cout = BATIMENTS[kind].cout
  const rendu: Cout = {}
  for (const cle of Object.keys(cout) as ItemId[]) {
    const n = Math.floor((cout[cle] ?? 0) * PART_REMBOURSEE)
    if (n > 0) rendu[cle] = n
  }
  return rendu
}

/** Vrai si ce joueur a le droit de demolir ce batiment. */
export function peutDemolir(b: Batiment, token: string, admin = false): boolean {
  return admin || b.proprietaire === token
}

/**
 * Ce que la demolition rend A CELUI QUI DEMOLIT.
 *
 * Le DECOR (identifiant `monde-...`, ou proprietaire `MONDE`) ne rend rien:
 * personne ne l'a paye, et le serveur le repose a chaque demarrage — le demolir
 * contre remboursement etait une source sans fond. L'administrateur qui demolit
 * chez quelqu'un d'autre ne se paie pas non plus. Meme fonction pour l'ecran et
 * pour le serveur: on affiche ce qu'il rendra.
 */
export function renduDemolition(b: Batiment, token: string): Cout {
  const decor = b.id.startsWith('monde-') || b.proprietaire === 'MONDE'
  return b.proprietaire === token && !decor ? remboursement(b.kind) : {}
}

/** Couleurs par defaut d'un modele, avec repli neutre. */
export function couleursDefaut(kind: BatimentKind): { murs: number; toit: number; porte: number } {
  return COULEURS_DEFAUT[kind] ?? COULEURS_NEUTRES
}

export function creerBatiment(
  kind: BatimentKind,
  pos: Vec2,
  rot: number,
  proprietaire: string,
  terrain: ITerrain,
  couleurs?: Partial<Batiment['couleurs']>,
): Batiment {
  const base = couleursDefaut(kind)
  return {
    id: idBatiment(kind, pos, rot, proprietaire),
    kind,
    pos: { x: pos.x, z: pos.z },
    y: terrain.hauteur(pos.x, pos.z),
    rot: ((rot % 4) + 4) % 4,
    proprietaire,
    couleurs: {
      murs: couleurs?.murs ?? base.murs,
      toit: couleurs?.toit ?? base.toit,
      porte: couleurs?.porte ?? base.porte,
    },
  }
}

// ------------------------------------------------------------------- interieur

/** Le batiment habitable qui contient ce point, sinon null. */
export function batimentContenant(batiments: Batiment[], x: number, z: number): Batiment | null {
  for (const b of batiments) {
    if (!BATIMENTS[b.kind].habitable) continue
    if (dansEmprise(b, x, z)) return b
  }
  return null
}

/**
 * Position de la porte.
 *
 * La porte est sur la face -z du modele. C'est la convention du rendu
 * (client/render/batiments.ts, `construireHabitation`) et donc celle que voit le
 * joueur: la simulation doit s'y conformer, pas l'inverse. Auparavant elle
 * repondait +z, et le jeu proposait donc d'entrer en butant contre le mur du fond.
 *
 * Un commentaire orphelin traînait juste au-dessus de celui-ci et affirmait le
 * CONTRAIRE — « le cote +z local ». Il decrivait l'ancienne version, fausse, et
 * se lisait en premier. Un commentaire perime sur un piege deja paye est pire
 * qu'un fichier sans commentaire du tout: il renvoie droit dans le piege.
 */
export function portePosition(b: Batiment): Vec2 {
  const t = BATIMENTS[b.kind].taille
  const v = tourner(0, -t.p / 2, b.rot)
  return { x: b.pos.x + v.x, z: b.pos.z + v.z }
}

/**
 * Ce qu'une maison contient, et OU.
 *
 * Le plan est le meme pour tous les modeles habitables, exprime en fractions de
 * l'emprise: la porte est au milieu de la face -z, le fond (+z) recoit ce qui se
 * pose contre un mur — cuisine a gauche, lit a droite —, l'avant (-z) ce qui
 * s'habite — salon a gauche, table a manger a droite. L'axe du seuil reste
 * libre, et le coin de l'animal se range contre le mur du fond, entre la cuisine
 * et le lit, hors des allees.
 *
 * Une seule table de fractions, une seule rotation: c'est ce qui garantit
 * qu'une maison tournee n'a pas son canape dans le mur. Ecrire les quatre cas a
 * la main est deja ce qui avait mis la porte du mauvais cote.
 */
export const MEUBLES_MAISON = ['cuisine', 'lit', 'salon', 'table', 'animal', 'pupitre', 'classe', 'filon'] as const

export type MeubleMaison = (typeof MEUBLES_MAISON)[number]

const ANCRAGE_MEUBLE: Record<MeubleMaison, Vec2> = {
  cuisine: { x: -0.25, z: 0.25 },
  lit: { x: 0.25, z: 0.25 },
  salon: { x: -0.25, z: -0.25 },
  table: { x: 0.25, z: -0.25 },
  animal: { x: 0, z: 0.32 },
  // Le pupitre prend EXACTEMENT la place du canape: c'est le coin qu'on habite
  // en entrant, et c'est la qu'on veut s'asseoir pour travailler.
  pupitre: { x: -0.25, z: -0.25 },
  // Les rangees d'ecoliers et le tableau, contre le mur du fond: du decor, au
  // meme titre que le salon. Rien ne s'y declenche.
  classe: { x: 0, z: 0.26 },
  /*
   * LE FRONT DE TAILLE de la mine: tout au fond de la galerie, dans l'axe de
   * l'entree. C'est un MEUBLE et pas un batiment reconnu par la proximite (la
   * facon dont l'ecole, le musee et la bibliotheque repondent) parce qu'on ne
   * pioche pas depuis le seuil: il faut avoir descendu la galerie et se tenir
   * devant la roche. `meublesDe` garde ce filon dans la SEULE mine — un
   * ancrage sans exception en aurait pose un dans toutes les maisons du monde,
   * la lecon deja payee par le pupitre.
   */
  filon: { x: 0, z: 0.34 },
}

/**
 * CE QUE CHAQUE MODELE CONTIENT, quand ce n'est pas une maison ordinaire.
 *
 * Le plan des meubles etait le MEME pour tous les habitables, et il l'etait par
 * defaut, sans que rien ne le dise. L'ecole avait donc une cuisine et un lit —
 * on entrait dans la classe et on pouvait s'y coucher — et le garage aurait eu
 * les quatre. C'est le piege deja paye ailleurs, celui de la regle ecrite pour
 * UN modele: on met la reponse dans la DONNEE, une entree par exception, et le
 * reste garde l'ameublement d'une maison.
 */
const AMEUBLEMENT: Partial<Record<BatimentKind, readonly MeubleMaison[]>> = {
  /*
   * L'hotel n'a du plan d'une maison que le LIT — celui de la chambre de
   * droite, a l'ancrage que `ANCRAGE_PAR_MODELE` lui donne. Pas de cuisine ni
   * de salon au rez-de-chaussee: le hall est un hall, la salle a manger est a
   * l'etage et s'atteint par l'ascenseur. La table n'est pas listee ici pour
   * la raison du portable de la villa: `meubleProche` ignore les niveaux, et
   * la proposer depuis le hall reviendrait a manger a travers la dalle.
   */
  hotel: ['lit'],
  // Ni cuisine ni lit: on ne dort pas a l'ecole. Le canape devient un PUPITRE,
  // ou l'on travaille, et le fond de la salle recoit la classe.
  ecole: ['pupitre', 'classe'],
  // Un garage se remplit d'une voiture, pas d'un salon.
  garage: [],
  /*
   * Le musee, la bibliotheque et le zoo n'ont RIEN de la table des meubles:
   * leur amenagement est propre a eux et vit dans `EXTRAS`
   * (client/render/batiments.ts) — cadres, rayonnages, enclos.
   *
   * L'entree vide n'est pas cosmetique. Sans elle, ils retombaient sur le plan
   * d'une maison: un lit et une cuisine dessines au milieu du musee, et surtout
   * le COIN DE L'ANIMAL, qui est ce que `peutAdopter` interroge — un animal
   * conduit dans un enclos du zoo etait donc adopte comme animal de compagnie
   * DU ZOO, et l'enclos n'etait jamais atteint. Une campagne de bout en bout a
   * pris ce defaut sur le fait.
   */
  musee: [],
  bibliotheque: [],
  zoo: [],
  // Une galerie ne se meuble pas: elle a un FRONT DE TAILLE, et rien d'autre.
  // Pas de lit surtout — on ne dort pas au fond d'une mine, et le coin de
  // l'animal y aurait fait adopter un lapin dans un puits.
  mine: ['filon'],
  /*
   * UNE AEROGARE N'EST PAS UNE MAISON: on n'y dort pas, on n'y cuisine pas, on
   * y passe. Son hall et son restaurant sont dessines par `EXTRAS`
   * (client/render/batiments.ts), et le restaurant se touche par le PLAN
   * (`restaurantProche`) parce qu'il est a l'etage — les meubles de cette
   * table-ci, eux, ignorent les niveaux.
   */
  aerodrome: [],
  aeroport: [],
  aeroport_verre: [],
  /*
   * UN PARC AQUATIQUE N'EST PAS UNE MAISON. Sans cette entree vide, il
   * retombait sur le plan d'une habitation: une cuisine et un lit au bord du
   * bassin, et surtout le COIN DE L'ANIMAL, que `peutAdopter` interroge — on y
   * aurait adopte un lapin dans le pavillon des maillots. C'est le defaut deja
   * pris sur le fait au musee et au zoo.
   */
  parc_aquatique: [],
}

const AMEUBLEMENT_MAISON: readonly MeubleMaison[] = ['cuisine', 'lit', 'salon', 'table', 'animal']

/** Les meubles de ce modele: son exception, sinon ceux d'une maison. */
export function meublesDe(kind: BatimentKind): readonly MeubleMaison[] {
  return AMEUBLEMENT[kind] ?? AMEUBLEMENT_MAISON
}

/** Vrai si ce modele possede ce meuble. */
export function aLeMeuble(kind: BatimentKind, meuble: MeubleMaison): boolean {
  return meublesDe(kind).includes(meuble)
}

/**
 * Les meubles que le joueur peut toucher, dans l'ordre ou on les propose.
 *
 * Le salon, la classe et le coin de l'animal n'y sont pas: ils meublent la
 * piece, ils ne declenchent rien. Les ajouter ici suffira le jour ou ils le
 * feront. Ce que ce tableau ne dit PAS, c'est quel modele les possede — c'est
 * `meublesDe` qui repond, et les deux se croisent dans `meubleProche`.
 */
export const MEUBLES_INTERACTIFS = ['cuisine', 'table', 'lit', 'pupitre', 'filon'] as const

export type MeubleInteractif = (typeof MEUBLES_INTERACTIFS)[number]

/**
 * ANCRAGES PARTICULIERS, par modele. L'hotel ne suit pas le plan d'une maison:
 * son lit interactif est au centre de la chambre de droite, sa table a manger
 * a l'etage. Une entree ici REMPLACE l'ancrage commun — la table commune reste
 * la reponse de tous les autres modeles.
 */
const ANCRAGE_PAR_MODELE: Partial<Record<BatimentKind, Partial<Record<MeubleMaison, Vec2>>>> = {
  hotel: { lit: LIT_HOTEL, table: TABLE_HOTEL },
}

/** Position monde d'un meuble de la maison, rotation comprise. */
export function positionMeuble(b: Batiment, meuble: MeubleMaison): Vec2 {
  const t = BATIMENTS[b.kind].taille
  const a = ANCRAGE_PAR_MODELE[b.kind]?.[meuble] ?? ANCRAGE_MEUBLE[meuble]
  const v = tourner(t.l * a.x, t.p * a.z, b.rot)
  return { x: b.pos.x + v.x, z: b.pos.z + v.z }
}

/** Position monde de la cuisine: au fond a gauche, loin de la porte. */
export function positionCuisine(b: Batiment): Vec2 {
  return positionMeuble(b, 'cuisine')
}

/** Position monde du lit: au fond a droite, loin de la porte. */
export function positionLit(b: Batiment): Vec2 {
  return positionMeuble(b, 'lit')
}

/** Position monde du salon: a l'avant a gauche, en entrant. */
export function positionSalon(b: Batiment): Vec2 {
  return positionMeuble(b, 'salon')
}

/** Position monde de la table a manger: a l'avant a droite, en entrant. */
export function positionTable(b: Batiment): Vec2 {
  return positionMeuble(b, 'table')
}

/**
 * Position monde du coin de l'animal (panier, gamelle).
 *
 * Expose ici et pas dans le rendu pour la meme raison que les autres meubles:
 * ce qu'on voit doit etre exactement ce avec quoi on interagit, et le jour ou
 * un animal de compagnie viendra s'y coucher, il aura la meme reference que le
 * panier dessine.
 */
export function positionAnimal(b: Batiment): Vec2 {
  return positionMeuble(b, 'animal')
}

/**
 * Position monde du front de taille, au fond de la galerie.
 *
 * Meme raison que les autres: la roche qu'on VOIT doit etre celle qu'on pioche.
 * Le rendu lit cette fonction, la cible aussi.
 */
export function positionFilon(b: Batiment): Vec2 {
  return positionMeuble(b, 'filon')
}

// ------------------------------------------------------------------- murs

/**
 * Largeur de l'ouverture de la porte, en metres. Doit rester en accord avec
 * `L_PORTE` de client/render/batiments.ts, sinon on traverserait un mur plein
 * ou on buterait contre une porte visiblement ouverte.
 */
export const LARGEUR_PORTE = 1.1

/**
 * Largeur de l'ouverture de CE modele: la sienne, sinon la porte ordinaire.
 *
 * Une seule fonction pour la question, appelee par la collision comme par le
 * rendu. Deux ecritures de la meme largeur, c'est un mur plein la ou l'on voit
 * une ouverture — le genre d'ecart que ce projet a deja paye.
 */
export function largeurPorte(kind: BatimentKind): number {
  return BATIMENTS[kind].porte ?? LARGEUR_PORTE
}

/** Epaisseur des murs, en accord avec le rendu. */
export const EPAISSEUR_MUR = 0.18

/**
 * Repousse un point hors de la couronne de murs d'une boite, en repere LOCAL
 * centre sur elle. Rend `null` quand il n'y a rien a corriger.
 *
 * Extrait de `resoudreMurs` mot pour mot: la maison, l'acrotere de la terrasse
 * et le bureau posent exactement la meme question, et deux ecritures de la meme
 * regle finissent toujours par diverger. `demiPorte` a zero veut dire « aucune
 * ouverture », et `versInterieur` force le sens de la poussee — c'est ce qui
 * distingue un mur (on sort du cote le plus proche) d'un garde-corps (on rentre
 * toujours, sinon on tombe du toit).
 */
function repousserBoite(
  lx: number,
  lz: number,
  demiL: number,
  demiP: number,
  rayon: number,
  epaisseur: number,
  demiPorte: number,
  versInterieur: boolean,
): Vec2 | null {
  const hx = demiL + rayon
  const hz = demiP + rayon
  if (Math.abs(lx) > hx || Math.abs(lz) > hz) return null

  const ix = Math.max(0.15, demiL - epaisseur - rayon)
  const iz = Math.max(0.15, demiP - epaisseur - rayon)
  if (Math.abs(lx) < ix && Math.abs(lz) < iz) return null

  // La porte est sur la face -z du modele: on y laisse passer librement.
  if (demiPorte > 0 && lz < 0 && Math.abs(lx) <= demiPorte) return null

  // Dans l'epaisseur du mur: sortir vers l'exterieur ou vers l'interieur,
  // selon le cote le plus proche.
  const coutSortie = Math.min(hx - Math.abs(lx), hz - Math.abs(lz))
  const coutEntree = Math.max(Math.abs(lx) - ix, 0) + Math.max(Math.abs(lz) - iz, 0)

  let nx = lx
  let nz = lz
  if (versInterieur || coutEntree <= coutSortie) {
    if (Math.abs(lx) > ix) nx = Math.sign(lx) * ix
    if (Math.abs(lz) > iz) nz = Math.sign(lz) * iz
  } else if (hx - Math.abs(lx) < hz - Math.abs(lz)) {
    nx = Math.sign(lx) * hx
  } else {
    nz = Math.sign(lz) * hz
  }
  return { x: nx, z: nz }
}

/**
 * Les murs d'UN NIVEAU d'un batiment a etage(s), en repere local.
 *
 * Deux temps, dans cet ordre — et l'ordre fait partie du contrat, comme celui
 * de `appliquerEntree`: l'acrotere d'abord, qui garde le bonhomme sur sa dalle
 * et ne s'ouvre nulle part, puis les pieces DE CE NIVEAU, chacune avec sa
 * porte. Le niveau vient de `piecesEtage`: la villa n'a que sa grande piece au
 * niveau 1, l'hotel a le bureau et la salle a manger au niveau 1, le salon de
 * lecture au niveau 2.
 *
 * Rend `null` quand rien n'a bouge, exactement comme `repousserBoite`: le
 * retour au repere du monde n'est pas neutre en virgule flottante
 * (`b.pos.x + (x - b.pos.x)` ne rend pas toujours `x`), et le faire pour rien a
 * chaque tick ferait deriver une position que personne ne pousse.
 */
function murEtage(b: Batiment, lx: number, lz: number, rayon: number, niveau: number): Vec2 | null {
  const t = BATIMENTS[b.kind].taille
  let x = lx
  let z = lz
  let touche = false

  // L'acrotere, tout autour du pont et des pieces, a CHAQUE niveau. Aucune
  // ouverture: une porte la-haut ferait sortir dans le vide.
  const dedans = repousserBoite(x, z, t.l / 2, t.p / 2, rayon, EPAISSEUR_PARAPET, 0, true)
  if (dedans) {
    x = dedans.x
    z = dedans.z
    touche = true
  }

  // Les pieces du niveau, leur porte sur LEUR face -z: meme convention que les
  // maisons. Ce sont ces ouvertures qui relient les pieces au pont — sans
  // elles, l'etage serait une chambre close et un pont ou l'on ne va jamais.
  for (const piece of piecesEtage(b.kind, niveau)) {
    const boite = boiteEtage(b.kind, piece)
    const hors = repousserBoite(
      x - boite.cx,
      z - boite.cz,
      boite.hx,
      boite.hz,
      rayon,
      EPAISSEUR_MUR,
      piece.porte / 2,
      false,
    )
    if (!hors) continue
    x = boite.cx + hors.x
    z = boite.cz + hors.z
    touche = true
  }

  return touche ? { x, z } : null
}

/**
 * Repousse le joueur hors des murs des maisons.
 *
 * On ne bloque PAS l'emprise des maisons dans la grille de navigation: sinon on
 * ne pourrait jamais entrer dans sa cuisine, et un joueur present a l'endroit ou
 * une maison se pose se retrouverait enferme dans une zone entierement bloquee,
 * incapable de bouger dans aucune direction (c'est arrive).
 *
 * A la place, on traite la couronne de murs comme un obstacle mince, avec une
 * ouverture devant la porte. Le joueur entre par la porte, ressort par la porte,
 * et n'est jamais piege: si on le trouve dans l'epaisseur d'un mur, on le pousse
 * du cote le plus proche.
 *
 * `niveau` DECIDE DE QUELS MURS ON PARLE, et c'est tout le mecanisme de
 * l'etage. Au sol, ce sont les murs des maisons, porte comprise. A l'etage, ce
 * sont ceux de la terrasse — acrotere sans ouverture, plus le bureau — et
 * SEULEMENT ceux du batiment sur lequel on se tient: les maisons du voisinage
 * sont trois metres plus bas, elles n'ont rien a repousser. Ce parametre voyage
 * sur le fil (voir `Entree.niv`) parce que le serveur, sinon, resoudrait le
 * joueur contre le rez-de-chaussee pendant qu'il marche sur le toit, et le
 * pousserait a chaque tick.
 *
 * Fonction pure: renvoie la position corrigee.
 */
export function resoudreMurs(
  batiments: readonly Batiment[],
  pos: Vec2,
  niveau = 0,
  rayon = PLAYER_RADIUS,
): Vec2 {
  let x = pos.x
  let z = pos.z

  for (const b of batiments) {
    const def = BATIMENTS[b.kind]
    if (!def.habitable) continue
    // A l'etage, seuls comptent les batiments qui MONTENT jusque-la: la
    // terrasse de la villa voisine est un niveau plus bas que le pont de
    // l'hotel, elle n'a rien a repousser.
    if (niveau > 0 && niveauxDe(b.kind) < niveau) continue

    // Repere local du batiment: on annule sa rotation.
    const local = tournerInverse(x - b.pos.x, z - b.pos.z, b.rot)

    const corrige =
      niveau > 0
        ? murEtage(b, local.x, local.z, rayon, niveau)
        : murRezDeChaussee(b, local.x, local.z, rayon)
    if (!corrige) continue

    // Retour au repere du monde.
    const monde = tourner(corrige.x, corrige.z, b.rot)
    x = b.pos.x + monde.x
    z = b.pos.z + monde.z
  }

  return { x, z }
}

/**
 * Ce point du REZ-DE-CHAUSSEE est-il dans l'epaisseur d'un mur (elargie de
 * `rayon`)? La question d'un VEHICULE, qui n'est pas un point: on la pose pour
 * chaque point de son emprise. Sans elle, la voiture ne connaissait que la
 * grille de navigation — ou les batiments ou l'on entre ne sont pas barres —
 * et elle traversait l'ecole de part en part (vu au telephone le 13 octobre).
 * Une porte plus large que la voiture reste franchissable (le garage, le
 * zoo, le parc aquatique); celle d'une maison, d'un metre dix, ne l'est pas.
 */
export function dansUnMur(batiments: readonly Batiment[], x: number, z: number, rayon: number): boolean {
  for (const b of batiments) {
    if (!BATIMENTS[b.kind].habitable) continue
    const local = tournerInverse(x - b.pos.x, z - b.pos.z, b.rot)
    if (murRezDeChaussee(b, local.x, local.z, rayon)) return true
  }
  return false
}

/** Les cloisons INTERIEURES de ce modele, ou rien. Voir shared/data/zoo.ts. */
export function cloisonsInterieures(kind: BatimentKind): readonly PieceInterieure[] {
  return PLAN_INTERIEUR[kind] ?? AUCUNE_CLOISON
}

const AUCUNE_CLOISON: readonly PieceInterieure[] = Object.freeze([])

/**
 * La piece qui contient ce point MONDE — cloison du rez-de-chaussee ou piece
 * d'etage, selon le niveau — sinon null.
 *
 * C'est la question de l'ITINERAIRE: pour rejoindre un point dans une piece,
 * ou pour la quitter, on passe par SA porte. Sans cette reponse, un chemin
 * trace tout droit se plantait dans la cloison d'une chambre de l'hotel, le
 * bonhomme glissait le long du mur et s'arretait hors de portee du lit — un
 * « Dormir » que rien ne declenchait ni n'expliquait.
 */
export function pieceAuPoint(
  b: Batiment,
  x: number,
  z: number,
  niveau: number,
): PieceEtage | null {
  const local = tournerInverse(x - b.pos.x, z - b.pos.z, b.rot)
  const pieces: readonly PieceEtage[] =
    niveau > 0 ? piecesEtage(b.kind, niveau) : cloisonsInterieures(b.kind)
  for (const piece of pieces) {
    const bo = boiteEtage(b.kind, piece)
    if (Math.abs(local.x - bo.cx) <= bo.hx && Math.abs(local.z - bo.cz) <= bo.hz) return piece
  }
  return null
}

/**
 * Les deux points qui franchissent la porte d'une piece, en repere MONDE:
 * `devant` juste dehors, `dedans` juste passe le seuil. La porte est sur la
 * face -z de la piece, comme partout.
 */
export function seuilPiece(b: Batiment, piece: PieceEtage): { devant: Vec2; dedans: Vec2 } {
  const bo = boiteEtage(b.kind, piece)
  const devant = tourner(bo.cx, bo.cz - bo.hz - 0.9, b.rot)
  const dedans = tourner(bo.cx, bo.cz - bo.hz + 0.7, b.rot)
  return {
    devant: { x: b.pos.x + devant.x, z: b.pos.z + devant.z },
    dedans: { x: b.pos.x + dedans.x, z: b.pos.z + dedans.z },
  }
}

/**
 * Les murs du REZ-DE-CHAUSSEE d'un batiment, en repere local.
 *
 * L'enceinte d'abord — c'est elle qui garde le bonhomme dedans ou dehors —
 * puis les CLOISONS INTERIEURES, s'il y en a. L'ordre fait partie du contrat,
 * comme celui de `appliquerEntree` et celui de `murEtage`.
 *
 * Les cloisons sont arrivees avec le zoo, dont les enclos sont des pieces de
 * son propre modele et non des clotures posees une a une. Elles se resolvent
 * exactement comme le bureau de la terrasse: on est pousse DEHORS, sauf devant
 * leur ouverture, qui est sur leur face -z. C'est ce qui permet d'entrer
 * s'occuper d'une bete au lieu de la regarder par-dessus une barriere.
 *
 * Rend `null` quand rien n'a bouge, pour la meme raison que `murEtage`: le
 * retour au repere du monde n'est pas neutre en virgule flottante, et le faire
 * pour rien a chaque tick ferait deriver une position que personne ne pousse.
 */
function murRezDeChaussee(b: Batiment, lx: number, lz: number, rayon: number): Vec2 | null {
  const def = BATIMENTS[b.kind]
  let x = lx
  let z = lz
  let touche = false

  const enceinte = repousserBoite(
    x,
    z,
    def.taille.l / 2,
    def.taille.p / 2,
    rayon,
    EPAISSEUR_MUR,
    largeurPorte(b.kind) / 2,
    false,
  )
  if (enceinte) {
    x = enceinte.x
    z = enceinte.z
    touche = true
  }

  for (const piece of cloisonsInterieures(b.kind)) {
    const boite = boiteEtage(b.kind, piece)
    const hors = repousserBoite(
      x - boite.cx,
      z - boite.cz,
      boite.hx,
      boite.hz,
      rayon,
      EPAISSEUR_MUR,
      piece.porte / 2,
      false,
    )
    if (!hors) continue
    x = boite.cx + hors.x
    z = boite.cz + hors.z
    touche = true
  }

  return touche ? { x, z } : null
}

/**
 * Direction sortante de la porte, dans le repere du monde.
 * La porte est sur la face -z du modele, tournee ensuite par quarts de tour.
 */
export function porteNormale(b: Batiment): Vec2 {
  // La porte est sur la face -z du modele: sa normale sortante est donc
  // simplement (0, -1) tournee comme le batiment. Ecrire les quatre cas a la
  // main m'a fait inverser le signe pour les rotations 1 et 3.
  return tourner(0, -1, b.rot)
}

/** Point de rendez-vous devant la porte, a l'exterieur. */
export function porteExterieur(b: Batiment, recul = 1.6): Vec2 {
  const p = portePosition(b)
  const n = porteNormale(b)
  return { x: p.x + n.x * recul, z: p.z + n.z * recul }
}

/** Point juste a l'interieur, une fois le seuil franchi. */
export function porteInterieur(b: Batiment, avance = 1.2): Vec2 {
  const p = portePosition(b)
  const n = porteNormale(b)
  return { x: p.x - n.x * avance, z: p.z - n.z * avance }
}
